import type { DailyCount } from '@bale-bot/types';
import {
  Box,
  Button,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { VIZ, formatDayLabel } from '../viz/tokens';

/**
 * Daily event volume — one series, so one hue and no legend: the heading
 * already says what is plotted.
 *
 * Only the peak is direct-labelled; every other value lives in the hover
 * tooltip and in the table view below, because a number on every column reads
 * as noise and gets ignored.
 */
export default function DailyBars({ data }: { data: DailyCount[] }) {
  const [showTable, setShowTable] = useState(false);

  const max = data.reduce((acc, point) => Math.max(acc, point.count), 0);
  const peakIndex = data.findIndex((point) => point.count === max && max > 0);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          peak {max.toLocaleString('en-US')} / day
        </Typography>
        <Button size="small" onClick={() => setShowTable((open) => !open)}>
          {showTable ? 'Hide values' : 'Show values'}
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          gap: `${VIZ.gap}px`,
          height: 176,
          // Hairline baseline, solid and recessive — the only axis rule drawn.
          borderBottom: `1px solid ${VIZ.baseline}`,
          pb: 0,
        }}
      >
        {data.map((point, index) => {
          const heightPercent = max > 0 ? (point.count / max) * 100 : 0;
          return (
            <Tooltip
              key={point.date}
              arrow
              placement="top"
              title={`${formatDayLabel(point.date)} · ${point.count.toLocaleString('en-US')} events`}
            >
              <Box
                sx={{
                  flex: '1 1 0',
                  maxWidth: VIZ.columnMaxWidth,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  alignItems: 'stretch',
                  cursor: 'default',
                }}
              >
                {index === peakIndex && (
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      color: 'text.secondary',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1,
                      mb: 0.5,
                    }}
                  >
                    {point.count}
                  </Typography>
                )}
                <Box
                  sx={{
                    height: `${Math.max(heightPercent, point.count > 0 ? 2 : 0)}%`,
                    // 4px rounded cap, square where it meets the baseline.
                    borderRadius: `${VIZ.radius}px ${VIZ.radius}px 0 0`,
                    bgcolor: point.count > 0 ? VIZ.series : 'transparent',
                    minHeight: point.count > 0 ? 2 : 0,
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {data.length > 0 ? formatDayLabel(data[0]!.date) : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {data.length > 0 ? formatDayLabel(data[data.length - 1]!.date) : ''}
        </Typography>
      </Stack>

      <Collapse in={showTable} unmountOnExit>
        <Table size="small" sx={{ maxWidth: 320 }}>
          <TableHead>
            <TableRow>
              <TableCell>Day</TableCell>
              <TableCell align="right">Events</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((point) => (
              <TableRow key={point.date}>
                <TableCell>{formatDayLabel(point.date)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {point.count.toLocaleString('en-US')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Stack>
  );
}